import { useCallback, useEffect, useState } from 'react'
import { Message } from '@arco-design/web-react'
import { useTranslation } from 'react-i18next'
import { getUserOAuthBindingsApi, unbindCustomOAuthApi } from '@/api/user'
import {
  bindCustomOAuth,
  bindDiscordOAuth,
  bindGitHubOAuth,
  bindLinuxDoOAuth,
  bindOidcOAuth,
} from '@/services/oauthBind'
import { useAuthStore } from '@/stores/authStore'
import { useStatusStore } from '@/stores/statusStore'
import type { CustomOAuthProvider, UserOAuthBinding } from '@/types'

export function useOAuthBindings(runSecure: <T>(fn: () => Promise<T>) => Promise<T>) {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const status = useStatusStore((s) => s.status)
  const [customBindings, setCustomBindings] = useState<UserOAuthBinding[]>([])
  const [loadingBindings, setLoadingBindings] = useState(false)
  const [unbindLoading, setUnbindLoading] = useState<Record<number, boolean>>({})

  const loadCustomBindings = useCallback(async () => {
    setLoadingBindings(true)
    try {
      const list = await getUserOAuthBindingsApi()
      setCustomBindings(Array.isArray(list) ? list : [])
    } catch {
      setCustomBindings([])
    } finally {
      setLoadingBindings(false)
    }
  }, [])

  useEffect(() => {
    void loadCustomBindings()
  }, [loadCustomBindings, user?.id])

  const isBound = (id?: string) => Boolean(id && id !== '')

  const bindProvider = async (key: string) => {
    try {
      switch (key) {
        case 'github':
          if (status?.github_client_id) await bindGitHubOAuth(status.github_client_id)
          break
        case 'discord':
          if (status?.discord_client_id) await bindDiscordOAuth(status.discord_client_id)
          break
        case 'linuxdo':
          if (status?.linuxdo_client_id) await bindLinuxDoOAuth(status.linuxdo_client_id)
          break
        case 'oidc':
          if (status?.oidc_authorization_endpoint && status?.oidc_client_id) {
            await bindOidcOAuth(status.oidc_authorization_endpoint, status.oidc_client_id)
          }
          break
        default:
          break
      }
    } catch (err: unknown) {
      Message.error(err instanceof Error ? err.message : t('personal.oauth.bindFailed'))
    }
  }

  const bindCustom = async (provider: CustomOAuthProvider) => {
    try {
      await bindCustomOAuth(provider)
    } catch (err: unknown) {
      Message.error(err instanceof Error ? err.message : t('personal.oauth.bindFailed'))
    }
  }

  const unbindCustom = async (providerId: number) => {
    setUnbindLoading((prev) => ({ ...prev, [providerId]: true }))
    try {
      await runSecure(() => unbindCustomOAuthApi(providerId))
      Message.success(t('personal.oauth.unbindSuccess'))
      await loadCustomBindings()
    } catch (err: unknown) {
      if (err instanceof Error && err.message !== t('用户取消了二次验证')) {
        Message.error(err.message || t('personal.oauth.unbindFailed'))
      }
    } finally {
      setUnbindLoading((prev) => ({ ...prev, [providerId]: false }))
    }
  }

  const getCustomBinding = (providerId: number) =>
    customBindings.find((b) => b.provider_id === providerId)

  return {
    user,
    status,
    customBindings,
    loadingBindings,
    unbindLoading,
    isBound,
    bindProvider,
    bindCustom,
    unbindCustom,
    getCustomBinding,
    reloadBindings: loadCustomBindings,
  }
}
